import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Stack,
  MenuItem
} from "@mui/material";
import { Add, Edit, DeleteOutline } from "@mui/icons-material";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  where,
  getDoc
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import Swal from "sweetalert2";

function BookingTypes() {

  const [userData, setUserData] = useState(null);
  const [propertyId, setPropertyId] = useState(null);

  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    bookingtype: "",
    status: "active"
  });

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, user => setUserData(user));
    return () => unsub();
  }, []);

  /* ================= FETCH PROPERTY ID ================= */
  useEffect(() => {
    const fetchRole = async () => {
      if (!userData) return;

      const roleRef = doc(db, "authroles", userData.uid);
      const snap = await getDoc(roleRef);

      if (snap.exists()) {
        setPropertyId(snap.data().propertyId || null);
      } else {
        Swal.fire("Error", "Property not assigned to this user", "error");
      }
    };

    fetchRole();
  }, [userData]);

  /* ================= FETCH BOOKING TYPES ================= */
  const fetchPlans = async () => {
    if (!propertyId) return;

    const q = query(
      collection(db, "bookingtypes"),
      where("propertyId", "==", propertyId)
    );

    const snap = await getDocs(q);
    setPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchPlans();
  }, [propertyId]);

  /* ================= SAVE ================= */
  const savePlan = async () => {
    if (!form.bookingtype) {
      Swal.fire("Required", "Booking Type is required", "warning");
      return;
    }

    if (!propertyId) {
      Swal.fire("Error", "Property not found", "error");
      return;
    }

    try {
      if (editId) {
        await updateDoc(doc(db, "bookingtypes", editId), {
          ...form
        });
        Swal.fire("Updated", "Booking Type updated", "success");
      } else {
        await addDoc(collection(db, "bookingtypes"), {
          ...form,
          propertyId,
          createdAt: serverTimestamp()
        });
        Swal.fire("Added", "Booking Type added", "success");
      }

      setOpen(false);
      resetForm();
      fetchPlans();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  /* ================= DELETE ================= */
  const deletePlan = async (id) => {
    const ok = await Swal.fire({
      title: "Delete Booking Type?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete"
    });

    if (ok.isConfirmed) {
      await deleteDoc(doc(db, "bookingtypes", id));
      Swal.fire("Deleted", "Booking Type removed", "success");
      fetchPlans();
    }
  };

  /* ================= HELPERS ================= */
  const resetForm = () => {
    setEditId(null);
    setForm({ bookingtype: "", status: "active" });
  };

  const openEdit = (plan) => {
    setEditId(plan.id);
    setForm({
      bookingtype: plan.bookingtype,
      status: plan.status
    });
    setOpen(true);
  };

  /* ================= UI ================= */
  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
      <Menu />

      <Box className="content-wrapper" flex={1} p={4}>

        <Stack direction="row" justifyContent="space-between" mb={3}>
          <Typography fontSize={26} fontWeight={800}>
            Booking Types
          </Typography>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
            disabled={!propertyId}
          >
            Add Booking Type
          </Button>
        </Stack>

        <Card sx={{ borderRadius: 4 }}>
          <CardContent>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Booking Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {plans.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.bookingtype}</TableCell>
                      <TableCell>
                        <Chip
                          label={p.status.toUpperCase()}
                          color={p.status === "active" ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openEdit(p)}>
                          <Edit />
                        </IconButton>
                        <IconButton color="error" onClick={() => deletePlan(p.id)}>
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {!plans.length && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No Booking Types found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editId ? "Edit Booking Type" : "Add Booking Type"}
          </DialogTitle>

          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Booking Type"
                value={form.bookingtype}
                onChange={(e) => setForm({ ...form, bookingtype: e.target.value })}
                fullWidth
                size="small"
              />

              <TextField
                select
                label="Status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                fullWidth
                size="small"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={savePlan}>
              {editId ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
}

export default BookingTypes;
